import { useState, useCallback, useEffect } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Download, RefreshCw, Trash2, SkipForward, X } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { FieldError } from "@/components/shared/field-error"
import { Badge } from "@/components/ui/badge"
import { DLQEntriesQuery } from "@/lib/graphql/queries"
import {
  ReplayDLQEntryMutation,
  ReplayAllDLQMutation,
  PurgeDLQMutation,
  SkipDLQEntryMutation,
} from "@/lib/graphql/mutations"
import { DLQEntryCreatedSubscription } from "@/lib/graphql/subscriptions"
import { purgeDLQSchema, type PurgeDLQFormValues } from "@/lib/schemas"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"

type DLQItem = ResultOf<typeof DLQEntriesQuery>["dlqEntries"]["nodes"][number]

const LIMIT = 25

export function DLQPage() {
  const posthog = usePostHog()
  const navigate = useNavigate()
  const { isConfigured } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const replayedParam = searchParams.get("replayed")
  const eventId = searchParams.get("eventId") ?? undefined
  const replayed = replayedParam === "all" ? undefined : replayedParam === "true" ? true : false
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [eventId, replayed, search])

  const [result, reexecute] = useQuery({
    query: DLQEntriesQuery,
    variables: { eventId, replayed, search, after: pagination.after, first: pagination.first },
  })
  const [, replayEntry] = useMutation(ReplayDLQEntryMutation)
  const [replayAllResult, replayAll] = useMutation(ReplayAllDLQMutation)
  const [, purge] = useMutation(PurgeDLQMutation)
  const [, skipEntry] = useMutation(SkipDLQEntryMutation)

  const [replayAllOpen, setReplayAllOpen] = useState(false)
  const [purgeOpen, setPurgeOpen] = useState(false)
  const [replayingId, setReplayingId] = useState<string | null>(null)
  const [skippingId, setSkippingId] = useState<string | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkReplaying, setBulkReplaying] = useState(false)
  const [bulkSkipping, setBulkSkipping] = useState(false)

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  const handleNewEntry = useCallback(
    (
      _prev: ResultOf<typeof DLQEntryCreatedSubscription> | undefined,
      response: ResultOf<typeof DLQEntryCreatedSubscription>,
    ) => {
      if (response?.dlqEntryCreated) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: DLQEntryCreatedSubscription, pause: !isConfigured }, handleNewEntry)

  const handleReplayEntry = async (id: string) => {
    setReplayingId(id)
    const res = await replayEntry({ id })
    setReplayingId(null)
    if (res.error) { toast.error(res.error.message); return }
    posthog?.capture("dlq_entry_replayed", { entry_id: id })
    toast.success("DLQ entry replayed")
    reexecute({ requestPolicy: "network-only" })
  }

  const handleSkipEntry = async (id: string) => {
    setSkippingId(id)
    const res = await skipEntry({ id })
    setSkippingId(null)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("DLQ entry skipped")
    reexecute({ requestPolicy: "network-only" })
  }

  const handleReplayAll = async () => {
    const res = await replayAll({})
    if (res.error) { toast.error(res.error.message); return }
    const count = res.data?.replayAllDLQ.deliveries ?? 0
    posthog?.capture("dlq_replayed_all", { count })
    toast.success(`Replayed ${count} entries`)
    setReplayAllOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkReplay = async () => {
    setBulkReplaying(true)
    let ok = 0
    let failed = 0
    for (const id of selectedKeys) {
      const res = await replayEntry({ id })
      if (res.error) failed++
      else ok++
    }
    setBulkReplaying(false)
    if (failed > 0) toast.error(`${failed} entries failed to replay`)
    if (ok > 0) toast.success(`Replayed ${ok} entries`)
    setSelectedKeys(new Set())
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkSkip = async () => {
    setBulkSkipping(true)
    let ok = 0
    let failed = 0
    for (const id of selectedKeys) {
      const res = await skipEntry({ id })
      if (res.error) failed++
      else ok++
    }
    setBulkSkipping(false)
    if (failed > 0) toast.error(`${failed} entries failed to skip`)
    if (ok > 0) toast.success(`Skipped ${ok} entries`)
    setSelectedKeys(new Set())
    reexecute({ requestPolicy: "network-only" })
  }

  const columns: Column<DLQItem>[] = [
    {
      header: "Event Type",
      cell: (row) => (
        <Link
          to={`/events/${row.event.id}`}
          className="text-xs font-mono hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.event.eventType}
        </Link>
      ),
    },
    {
      header: "Destination",
      cell: (row) => (
        <Link
          to={`/destinations/${row.delivery.destination.id}`}
          className="text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.delivery.destination.name}
        </Link>
      ),
    },
    {
      header: "Error",
      cell: (row) => (
        <span className="text-xs text-destructive max-w-75 truncate block font-mono">
          {row.lastError}
        </span>
      ),
    },
    {
      header: "Replayed",
      cell: (row) =>
        row.replayedAt ? (
          <StatusBadge status="DELIVERED" />
        ) : (
          <StatusBadge status="PENDING" />
        ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) =>
        !row.replayedAt ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => { e.stopPropagation(); handleReplayEntry(row.id) }}
              disabled={replayingId === row.id}
              aria-label="Replay entry"
              title="Replay"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${replayingId === row.id ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => { e.stopPropagation(); handleSkipEntry(row.id) }}
              disabled={skippingId === row.id}
              aria-label="Skip entry"
              title="Skip"
            >
              <SkipForward className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : null,
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Dead Letter Queue" description="Failed deliveries for review and replay">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.dlqEntries.nodes ?? []).map((e) => ({
                id: e.id, eventType: e.event.eventType, destination: e.delivery.destination.name,
                lastError: e.lastError, replayed: !!e.replayedAt, createdAt: e.createdAt,
              }))
              exportJSON(rows, "dlq")
            }}>Export JSON</DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.dlqEntries.nodes ?? []).map((e) => ({
                id: e.id, eventType: e.event.eventType, destination: e.delivery.destination.name,
                lastError: e.lastError, replayed: e.replayedAt ? "yes" : "no", createdAt: e.createdAt,
              }))
              exportCSV(rows, ["id", "eventType", "destination", "lastError", "replayed", "createdAt"], "dlq")
            }}>Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={() => setPurgeOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Purge
        </Button>
        <Button size="sm" onClick={() => setReplayAllOpen(true)} className="shadow-primary-glow transition-shadow">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Replay All
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={replayedParam ?? "false"}
          onValueChange={(v) => setFilter("replayed", v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="Unreplayed" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Unreplayed</SelectItem>
            <SelectItem value="true">Replayed</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search DLQ..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />

        {eventId && (
          <Badge variant="secondary" className="gap-1 text-xs">
            Event: {eventId.slice(0, 8)}...
            <button type="button" onClick={() => setFilter("eventId", "")} className="ml-0.5 hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>

      {selectedKeys.size > 0 && (
        <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
          <Button
            size="sm"
            onClick={handleBulkReplay}
            disabled={bulkReplaying}
            className="shadow-primary-glow transition-shadow"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${bulkReplaying ? "animate-spin" : ""}`} />
            Replay Selected
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBulkSkip}
            disabled={bulkSkipping}
          >
            <SkipForward className="h-3.5 w-3.5 mr-1.5" />
            Skip Selected
          </Button>
        </BulkActionBar>
      )}

      <DataTable
        columns={columns}
        data={result.data?.dlqEntries.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.dlqEntries.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="DLQ is empty"
        emptyDescription="Failed deliveries will appear here"
        onRowClick={(row) => navigate(`/deliveries/${row.deliveryId}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <ConfirmDialog
        open={replayAllOpen}
        onOpenChange={setReplayAllOpen}
        title="Replay All DLQ Entries"
        description="This will re-create deliveries for all unreplayed DLQ entries."
        confirmLabel="Replay All"
        onConfirm={handleReplayAll}
        loading={replayAllResult.fetching}
      />

      <PurgeDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        onPurge={async (olderThan) => {
          const res = await purge({ olderThan })
          if (res.error) { toast.error(res.error.message); return }
          toast.success(`Purged ${res.data?.purgeDLQ.purged ?? 0} entries`)
          setPurgeOpen(false)
          reexecute({ requestPolicy: "network-only" })
        }}
      />
    </div>
  )
}

function PurgeDialog({
  open,
  onOpenChange,
  onPurge,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPurge: (olderThan: string) => Promise<void>
}) {
  const form = useForm<PurgeDLQFormValues>({
    resolver: zodResolver(purgeDLQSchema),
    defaultValues: { olderThan: "168h" },
  })

  useEffect(() => {
    if (open) form.reset()
  }, [open, form])

  const onSubmit = async (values: PurgeDLQFormValues) => {
    await onPurge(values.olderThan)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Purge DLQ Entries</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="olderThan">Older Than</Label>
            <Input
              id="olderThan"
              className="font-mono"
              placeholder="168h"
              {...form.register("olderThan")}
            />
            <FieldError error={form.formState.errors.olderThan} />
            <p className="text-xs text-muted-foreground">Duration format: 168h (7 days), 24h, 60m</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="destructive" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "..." : "Purge"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
